import { ExtensionEnvironment, type CapabilitySetType } from '@atlassian/forge-ui-types';

import type { GQLExtension } from '../../graphql/types';

const cache = new Map<string, GQLExtension[]>();

export const transformPreFilteredExtensions = (extensions?: GQLExtension[]): GQLExtension[] => {
	const extensionsIds = (extensions || []).map((ext) => ext.id).join(',');

	if (cache.has(extensionsIds)) {
		return cache.get(extensionsIds)!;
	}

	const result = (extensions || []).flatMap((extension): GQLExtension[] => {
		if (!extension) {
			return [];
		}

		if (!extension.environmentKey) {
			return [];
		}

		return [
			{
				id: extension.id,
				appId: extension.appId,
				key: extension.key,
				environmentId: extension.environmentId,
				environmentType:
					extension.environmentType === ExtensionEnvironment.DEVELOPMENT ||
					extension.environmentType === ExtensionEnvironment.STAGING ||
					extension.environmentType === ExtensionEnvironment.PRODUCTION
						? extension.environmentType
						: ExtensionEnvironment.DEVELOPMENT,
				environmentKey: extension.environmentKey,
				properties: extension.properties.reduce(
					(acc: { [x: string]: any }, property: { key: string | number; value: string }) => {
						if (!property.value || !property.key) {
							return acc;
						}

						try {
							const jsonValue = JSON.parse(property.value);
							acc[property.key] = jsonValue;
						} catch (_error) {
							acc[property.key] = property.value;
						}
						return acc;
					},
					{},
				),
				license: extension.license
					? {
							...extension.license,
							capabilitySet: mapCapabilitySet(extension.license.capabilitySet),
						}
					: null,
				userAccess: extension.userAccess
					? {
							hasAccess: extension.userAccess.hasAccess ?? false,
							enabled: extension.userAccess.enabled ?? false,
						}
					: null,
				type: extension.type,
				installationId: extension.installationId,
				appVersion: extension.appVersion ?? undefined,
				consentUrl: extension.consentUrl ?? undefined,
				migrationKey: extension.migrationKey ?? undefined,
				egress:
					extension.egress?.flatMap((egress) => {
						if (!egress.type || !egress.addresses) {
							return [];
						}

						return [
							{
								type: egress.type,
								addresses: egress.addresses?.flatMap((address) => address ?? []),
								category: egress.category ?? undefined,
								inScopeEUD: egress.inScopeEUD ?? undefined,
							},
						];
					}) ?? undefined,
				scopes: extension.scopes ?? undefined,
				installationConfig:
					extension.installationConfig?.flatMap((config) => {
						if (!config?.key || config.value === null) {
							return [];
						}

						return [
							{
								key: config.key,
								value: config.value,
							},
						];
					}) ?? undefined,
				dataClassificationPolicyDecision: {
					status: extension.hiddenBy === 'APP_ACCESS_RULES' ? 'BLOCKED' : 'ALLOWED',
				},
			},
		];
	});

	const IS_TESTING_ENV = process.env.NODE_ENV === 'test' || process.env.JEST_WORKER_ID !== undefined;
	if (!IS_TESTING_ENV) {
		cache.set(extensionsIds, result);
	}

	return result;
};

const mapCapabilitySet = (capabilitySet: string): CapabilitySetType => {
	switch (capabilitySet) {
		case 'CAPABILITY_ADVANCED':
			return 'capabilityAdvanced';
		default:
			return 'capabilityStandard';
	}
};
